import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaServer, FaSpinner } from 'react-icons/fa';
import RouterSetupWizard from './RouterSetupWizard';
import './RouterConfigurationPage.scss';

/**
 * Router Configuration Page
 *
 * Shows a list of configured routers with options to:
 * - Edit an existing router (opens wizard in edit mode)
 * - Add a new router (opens wizard in create mode)
 * - Delete a router
 */
const RouterConfigurationPage = () => {
  const { apiFetch } = useAuth();
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Load routers on mount
  useEffect(() => {
    loadRouters();
  }, []);

  const loadRouters = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/RouterConfig');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRouters(data.data || []);
        }
      } else {
        toast.error('Failed to load routers');
      }
    } catch (err) {
      console.error('Error loading routers:', err);
      toast.error('Failed to load routers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRouter = () => {
    setSelectedRouter(null);
    setShowWizard(true);
  };

  const handleEditRouter = (router) => {
    setSelectedRouter(router);
    setShowWizard(true);
  };

  const handleDeleteRouter = async (routerId) => {
    if (!window.confirm('Are you sure you want to delete this router?')) {
      return;
    }

    setDeleting(routerId);
    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Router deleted successfully');
        loadRouters();
      } else {
        toast.error('Failed to delete router');
      }
    } catch (err) {
      console.error('Error deleting router:', err);
      toast.error('Failed to delete router');
    } finally {
      setDeleting(null);
    }
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
    setSelectedRouter(null);
    loadRouters();
  };

  // Show wizard when user opens it
  if (showWizard) {
    return (
      <div className="router-config-page">
        <div className="router-wizard-container">
          <button
            className="btn-back-to-list"
            onClick={() => setShowWizard(false)}
          >
            ← Back to Routers
          </button>
          <RouterSetupWizard
            onComplete={handleWizardComplete}
            existingRouter={selectedRouter}
          />
        </div>
      </div>
    );
  }

  // Show list view
  return (
    <div className="router-config-page">
      <div className="router-config-header">
        <h1>Router Configuration & Setup</h1>
        <button
          className="btn btn-primary"
          onClick={handleAddRouter}
        >
          <FaPlus /> Add Router
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <FaSpinner className="spinner" /> Loading routers...
        </div>
      ) : routers.length === 0 ? (
        <div className="empty-state">
          <FaServer size={48} />
          <h2>No Routers Configured</h2>
          <p>Get started by adding your first router.</p>
          <button
            className="btn btn-primary"
            onClick={handleAddRouter}
          >
            <FaPlus /> Add Your First Router
          </button>
        </div>
      ) : (
        <div className="routers-grid">
          {routers.map(router => (
            <div key={router.id} className="router-card">
              <div className="router-card-header">
                <h3>{router.displayName}</h3>
                <span className={`status-badge ${router.isEnabled ? 'enabled' : 'disabled'}`}>
                  {router.isEnabled ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="router-card-details">
                <div className="detail-row">
                  <span className="detail-label">IP Address:</span>
                  <span className="detail-value">{router.ipAddress}</span>
                </div>
                {router.location && (
                  <div className="detail-row">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{router.location}</span>
                  </div>
                )}
                {router.propertyName && (
                  <div className="detail-row">
                    <span className="detail-label">Property:</span>
                    <span className="detail-value">{router.propertyName}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`status-text ${router.provisioningStatus?.toLowerCase()}`}>
                    {router.provisioningStatus || 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="router-card-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => handleEditRouter(router)}
                  title="Edit router configuration"
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteRouter(router.id)}
                  disabled={deleting === router.id}
                  title="Delete router"
                >
                  {deleting === router.id ? (
                    <><FaSpinner className="spinner" /> Deleting...</>
                  ) : (
                    <><FaTrash /> Delete</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RouterConfigurationPage;
